import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { EyeCheckboxButton, PenCheckboxButton } from '../CheckboxButton.tsx';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ViewsService } from '@/engine/app/Views.service';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { useTheme } from '@mui/material/styles';
import { StyledTableCell, UpdatableResetIconButton } from '../BoxTools';
import type { BasicBoxProps, MeasurementProps } from './types';
import { useHighlight } from '../hooks/useHighlight';
import { useSelect } from '../hooks/useSelect';
import { UpdatableValue } from '../common/UpdatableValue.tsx';
import { getConditionalStyles } from '../styles';
import type { MeasurementBoxesController } from '.';

type AreaMeasurementBoxProps = BasicBoxProps<MeasurementProps>;

enum AreaValueType {
  area = 'area',
  dMin = 'dMin',
  dMax = 'dMax',
  dMean = 'dMean'
}

export const AreaMeasurementBox: React.FC<AreaMeasurementBoxProps> = (props) => {
  const theme = useTheme();

  const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const measurementVMs = viewsService.currentView?.getMeasurements();

  const { isHighlighted } = useHighlight(measurementVMs);
  const { isSelected } = useSelect(measurementVMs);

  return (
    <TableContainer component={Paper}>
      <Table aria-label="a dense table" size="small">
        <TableHead>
          <TableRow key={'header'} sx={{ '& th': { fontSize: '0.7em', paddingLeft: 0.2 } }}>
            <TableCell></TableCell>
            <TableCell></TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, paddingRight: '0.2em', width: '4em' }}>
              Min&nbsp;Ø
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, paddingRight: '0.2em', width: '4em' }}>
              Max&nbsp;Ø
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, paddingRight: '0.2em', width: '4em' }}>
              Mean&nbsp;Ø
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, paddingRight: 0, width: '7em' }}>
              Area
            </TableCell>
            <TableCell align="right" sx={{ '&': { paddingRight: 0.5, paddingLeft: 0 } }}></TableCell>
            <TableCell align="right" sx={{ '&': { paddingRight: 0.5, paddingLeft: 0 } }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.measurements.map((measurement: MeasurementProps, index) => {
            const isItemSelected: boolean = isSelected(measurement.descriptor);
            const isItemHighlighted: boolean = isHighlighted(measurement.descriptor);

            return (
              <TableRow
                key={measurement.label + index}
                data-testid={measurement.descriptor + 'Area'}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  ...getConditionalStyles(
                    isItemHighlighted,
                    theme,
                    {
                      paddingLeft: 0.2,
                      paddingTop: 0,
                      paddingBottom: 0,
                      paddingRight: '0.1em'
                    },
                    {
                      paddingRight: 0.2,
                      paddingTop: 0,
                      paddingBottom: 0,
                      width: '5.35rem'
                    }
                  )
                }}
                onMouseEnter={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, true)}
                onMouseLeave={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, false)}
                selected={isItemSelected}
                onClick={(): void => {
                  mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
                }}
              >
                <TableCell component="th" scope="row" key={'label' + index}>
                  {measurement.label}
                </TableCell>
                <StyledTableCell className="limited-width light-fill">
                  <UpdatableResetIconButton
                    descriptor={measurement.descriptor}
                    controller={mainController}
                    attachmentNumber={0}
                  />
                </StyledTableCell>
                <TableCell align="right" key={'min' + index}>
                  <UpdatableValue
                    descriptor={measurement.descriptor}
                    constructor={DT.AreaMeasurement}
                    valueKey={AreaValueType.dMin}
                  />
                </TableCell>
                <TableCell align="right" key={'max' + index}>
                  <UpdatableValue
                    descriptor={measurement.descriptor}
                    constructor={DT.AreaMeasurement}
                    valueKey={AreaValueType.dMax}
                  />
                </TableCell>
                <TableCell align="right" key={'mean' + index}>
                  <UpdatableValue
                    descriptor={measurement.descriptor}
                    constructor={DT.AreaMeasurement}
                    valueKey={AreaValueType.dMean}
                  />
                </TableCell>
                <TableCell align="right" key={'area' + index} sx={{ '&': { paddingRight: 0 } }}>
                  <UpdatableValue
                    constructor={DT.AreaMeasurement}
                    descriptor={measurement.descriptor}
                    valueKey={AreaValueType.area}
                  />{' '}
                  mm²
                </TableCell>
                <StyledTableCell
                  align="right"
                  key={'beingEdited' + index}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  {measurement.beingEdited !== undefined ? (
                    <PenCheckboxButton
                      onChange={(checked): void => {
                        mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                      }}
                      checked={measurement.beingEdited}
                      key={'beingEditedBtn' + index}
                    />
                  ) : (
                    <div />
                  )}
                </StyledTableCell>
                <StyledTableCell
                  align="right"
                  key={'visibility' + index}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  <EyeCheckboxButton
                    onChange={(visible): void =>
                      mainController.setMeasurementVisibility(measurement.descriptor, visible)
                    }
                    checked={measurement.visible}
                  />
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
